

/* global google */

import React, { useEffect, useState, useRef, useCallback } from 'react';

import { useImperativeHandle, forwardRef } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { gmApiKey } from '../../parseConfig';

import DraggableOverlay from '../helpers/DraggableOverlay';

import useUrlSync from '../helpers/urlSyncHelper';
import { setupMapListeners } from '../helpers/urlSyncHelper';
import { initRotationUtils, getCurrentRotation } from '../helpers/rotationUtils';
import { replaceUrlState } from '../helpers/urlUpdaterHelper';

import TopButtons from './UITopButtons';
import Crosshairs from './Crosshairs';
import UIBottomButtons from './UIBottomButtons';
import PlacesSearchModal from './PlacesSearchModal';
import washedOutMapStyle from '../styles/washedOutMapStyle';  // Make it like the Uber map.

import { fetchParcelBoundaryFromLINZ } from '../helpers/fetchParcelBoundaryFromLINZ';
import { drawParcelBoundaries, clearParcel } from '../helpers/drawParcelBoundaries';

import saveDroppedPlan from '../helpers/saveDroppedPlanTrackingObject';


/*-----------------------------------------------------------------------------*/


// Keep this constant outside the component so its reference never changes
const GMAPS_LIBRARIES = ['places', 'geometry'];

// Centre coordinates for the map
const specialCoordinates = {
  lat: -41.272785,  // Botanical hill Nelson
  lng: 173.299477  // Botanical hill Nelson
  //lat: -43.423845,  // Fraemohs
  //lng: 172.651416  // Fraemohs
};

const newZealandBounds = {
  north: -33.8,
  south: -47.5,
  west: 166.5,
  east: 179.5
};

const containerStyle = {
  width: '100%',
  height: '100%'
};


// The MapComponent is a functional React component that uses forwardRef to pass a ref to its parent
const MapComponent = forwardRef((props, ref) => {

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps',
    googleMapsApiKey: gmApiKey,
    libraries: GMAPS_LIBRARIES,
  });

  const [mapsLoaded, setMapsLoaded] = useState(false); // Initialize mapsLoaded to false

  const {
    mapRef,
    topLayerRef,
    layers,
    selectedBaseMap,
    isParcelBoundariesVisible,
    setIsParcelBoundariesVisible,
    selectedPlanId,
    setSelectedPlanId,
    activeOverlayPlanId,
    overlayCentreRef,
    plansBySeries,
    okayToUpdateURLInt,
    onLandSelect,
    presentationModeiFrame
  } = props;

  const [overlayImageUrl, setOverlayImageUrl] = useState(null);
  const boundsRef = useRef(null);
  const [bounds, setBounds] = useState(null);

  const [showOverlay, setShowOverlay] = useState(false); // Initially false
  const [showCrosshairs, setShowCrosshairs] = useState(true); // State to control crosshairs visibility

  // URL arguments
  const urlParams = new URLSearchParams(window.location.search);
  const initialPlanId = urlParams.get('p');
  const dontShowSearchInt = urlParams.get('dss');
  //const dontWaitToDropInt = urlParams.get('dwtd');
  const latParamFloat = parseFloat(urlParams.get('lat'));
  const lngParamFloat = parseFloat(urlParams.get('lng'));
  const zoomParam = parseInt(urlParams.get('z'));
  const rotationParam = parseFloat(urlParams.get('r') || '0');

  // Set map centre on load.
  const { center: centerProp } = props; // Get center props

  const [mapCenter, setMapCenter] = useState(() => {  // Either use the URL parameters or the centerProp or the hard coded default coordinates.
    return (latParamFloat && lngParamFloat)
      ? { lat: latParamFloat, lng: lngParamFloat }
      : centerProp || specialCoordinates;
  });

  // Rotation
  const [rotation, setRotation] = useState(() => {  // If there's a plan and rotation argument in the URL, use it.
    return initialPlanId && !isNaN(rotationParam) ? rotationParam : 0;
  });

  const isShiftDraggingRef = useRef(false);

  const rotationRef = useRef(0);

  // Long click for land parcel data.
  const longClickTimeoutRef = useRef(null);
  const clickStartCoordsRef = useRef(null);

  // Mirroring
  const mirroredFromUrl = urlParams.get('mr') === '1';
  const [overlayIsMirrored, setOverlayIsMirrored] = useState(mirroredFromUrl);

  const userInitiatedPlanSelectionRef = useRef(false);  // This gatekeeper controls when we log the dropped plans in Parse.



  /*-----------------------------------------------------------------------------*/


  useEffect(() => {
    initRotationUtils(rotationRef, setRotation);
  }, []);

  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);


  // This triggers the URL helpers to update the URL as the various states that we are interested in change.
  useUrlSync({
    mapRef,
    activeOverlayPlanId,
    rotation,
    okayToUpdateURLInt
  });


  // Search modal.
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(() => {  // Don't show the search modal on load if there is a plan in the URL.
    return !(dontShowSearchInt === '1');
  });


  const handlePlaceSelected = (newCenter) => {

    setMapCenter(newCenter);  // Move the map to the new search result

    // Reset UI state
    setSelectedPlanId(null);
    setShowOverlay(false);
    setOverlayImageUrl(null);
    setBounds(null);
    setShowCrosshairs(true);
    overlayCentreRef.current = null;
    setRotation(0);

    // Clear any land parcel data.
    if (props.onLandSelect) {
      props.onLandSelect(null);
    }

    const url = new URL(window.location);

    // Write the new map centre and zoom to the URL.
    url.searchParams.set('lat', newCenter.lat.toFixed(6));
    url.searchParams.set('lng', newCenter.lng.toFixed(6));
    url.searchParams.set('z', 20);

    url.searchParams.set('dss', 1);  // Since we've found our site, on reload we don't need to show the search box.

    // Since we're clearing the overlay remove related arguments from the URL.
    url.searchParams.delete('p');
    url.searchParams.delete('r');
    url.searchParams.delete('olat');
    url.searchParams.delete('olng');
    url.searchParams.delete('dwtd');

    replaceUrlState(url);
  };


  // Used for the drop plan button only.
  const handleDropPlanClick = () => {

    if (!activeOverlayPlanId) return;

    const currentMapCenter = mapRef.current?.getCenter();

    if (currentMapCenter) {

      overlayCentreRef.current = {

        lat: currentMapCenter.lat(),
        lng: currentMapCenter.lng()
      };
    }

    setRotation(0);  // Reset rotation to 0

    const url = new URL(window.location);
    url.searchParams.set('olat', currentMapCenter.lat().toFixed(6));
    url.searchParams.set('olng', currentMapCenter.lng().toFixed(6));
    url.searchParams.set('r', '0');
    replaceUrlState(url);

    userInitiatedPlanSelectionRef.current = true;    // <- allow logging when selectedPlanId updates
    setSelectedPlanId(activeOverlayPlanId); // triggers useEffect to show overlay
  };


  // Update the overlay on the map when selectedPlanId is set.
  useEffect(() => {

    if (!selectedPlanId || !plansBySeries || !mapRef.current) {
      setOverlayImageUrl(null);
      setBounds(null);
      setShowOverlay(false);
      setShowCrosshairs(true); // optional
      return;
    }

    const allPlans = Object.values(plansBySeries).flat();
    const selected = allPlans.find(plan => plan.id === selectedPlanId);

    if (!selected) return;

    const series = Object.entries(plansBySeries).find(([, plans]) =>
      plans.some(p => p.id === selectedPlanId)
    )?.[0];

    let file, overlayX, overlayY;

    if (series === 'Accessory') {
      file = selected.get('file');
      overlayX = selected.get('overlayX');
      overlayY = selected.get('overlayY');
    } else {
      const planFile = selected.get('planFile');
      file = planFile?.get('file');
      overlayX = planFile?.get('overlayX');
      overlayY = planFile?.get('overlayY');
    }

    if (!file || !overlayX || !overlayY) return;

    const mapCurrentCentre = mapRef.current.getCenter();

    const cntr = overlayCentreRef.current || {
      lat: mapCurrentCentre.lat(),
      lng: mapCurrentCentre.lng()
    };

    const overlayBounds = {
      north: cntr.lat + (overlayY / 2) / 111000,
      south: cntr.lat - (overlayY / 2) / 111000,
      east: cntr.lng + (overlayX / 2) / (111000 * Math.cos(cntr.lat * Math.PI / 180)),
      west: cntr.lng - (overlayX / 2) / (111000 * Math.cos(cntr.lat * Math.PI / 180))
    };

    setOverlayImageUrl(file.url());
    boundsRef.current = overlayBounds;
    setBounds(overlayBounds);
    setShowOverlay(true);
    setShowCrosshairs(false);

    // Log the dropped plan to Parse for Amanda's marketing purposes.
    if (userInitiatedPlanSelectionRef.current) {

      userInitiatedPlanSelectionRef.current = false;  // reset immediately    

      const selected = Object.values(plansBySeries).flat().find(p => p.id === selectedPlanId);

      if (selected) {

        saveDroppedPlan({
          lat: cntr.lat,
          lng: cntr.lng,
          planId: selectedPlanId,
          plansBySeries,
          presentationModeiFrame: presentationModeiFrame
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId, plansBySeries, mapRef, overlayCentreRef]);


  // Also update the url as the selected plan changes.
  useEffect(() => {

    if (!selectedPlanId) return;

    const url = new URL(window.location);

    url.searchParams.set('p', selectedPlanId);
    url.searchParams.set('dwtd', '1');  // Once we have a plan on screen if we use this URL elsewhere we want it to appear straight away.
    url.searchParams.set('dss', '1');  // Once we have a plan on screen if we use this URL elsewhere we don't want to load the seach modal.
    replaceUrlState(url);

  }, [selectedPlanId]);


  // Function to change the base map
  const changeBaseMap = useCallback((baseMap) => {

    if (mapRef.current && layers.secondary) {

      mapRef.current.overlayMapTypes.clear(); // Clear existing map types

      let baseMapType;

      // Find the layer corresponding to the base map name
      const layer = layers.secondary.find(layer => layer.get('name') === baseMap);

      if (layer) {
        if (layer.get('format')) {

          // Create a new ImageMapType for the base map layer
          baseMapType = new window.google.maps.ImageMapType({

            getTileUrl: function (coord, zoom) {

              const url = layer.get('format')
                .replace('{z}', zoom)
                .replace('{x}', coord.x)
                .replace('{y}', coord.y);
              //console.log("Base map tile URL:", url);
              return url;
            },
            tileSize: new window.google.maps.Size(256, 256),
            maxZoom: 22,
            name: layer.get('name')
          });
          mapRef.current.overlayMapTypes.push(baseMapType); // Add new base map type
        }
        else {
          //console.log("MapComponent.js: (No format) Setting map type ID to:", layer.get('name'));
          switch (layer.get('name')) {

            case 'Google Maps Satellite':
              requestAnimationFrame(() => {
                mapRef.current?.setMapTypeId('satellite');
              });
              break;

            case 'Google Maps Base Layer':
              mapRef.current.setMapTypeId('roadmap');
              break;

            default:
              mapRef.current.setMapTypeId('roadmap');
          }
        }
      }
      else if (baseMap === 'None') {
        mapRef.current.setMapTypeId('roadmap'); // Default to roadmap if no base map selected
      }

      // Re-add the parcel boundaries layer if it is supposed to be visible
      if (topLayerRef.current && isParcelBoundariesVisible) {
        //console.log("Re-adding parcel boundaries layer");
        mapRef.current.overlayMapTypes.push(topLayerRef.current);
      }

      const url = new URL(window.location);  // Update the URL to suit.

      let baseMapIndex;

      if (baseMap === 'None') {
        baseMapIndex = -1;
      } else {
        baseMapIndex = layers.secondary.findIndex(layer => layer.get('name') === baseMap);
      }

      url.searchParams.set('bm', baseMapIndex);
      replaceUrlState(url);
    }
  }, [mapRef, layers.secondary, topLayerRef, isParcelBoundariesVisible]);


  useImperativeHandle(ref, () => ({  // useImperativeHandle to expose functions to the parent component
    changeBaseMap,
    toggleParcelBoundaries,
    isMapReady: () => !!mapRef.current,
    clearLandOverlay,
    markPlanSelectionUserInitiated: () => {  // We only log a dropped plan if the user made the selection. Restoring state from the URL, or programatic refreshes don't count.
      userInitiatedPlanSelectionRef.current = true;
    }
  }));


  // Function to toggle the visibility of parcel boundaries
  const toggleParcelBoundaries = (isVisible) => {

    setIsParcelBoundariesVisible(isVisible); // Update visability state

    if (mapRef.current && topLayerRef.current) {
      if (isVisible) {
        if (mapRef.current.overlayMapTypes.getArray().indexOf(topLayerRef.current) === -1) {
          mapRef.current.overlayMapTypes.push(topLayerRef.current);  // Add the layer if it's not already added
        }
      } else {
        const index = mapRef.current.overlayMapTypes.getArray().indexOf(topLayerRef.current);
        if (index !== -1) {
          mapRef.current.overlayMapTypes.removeAt(index);  // Remove the layer if it's already added
        }
      }
    }

    // Fix a glitch specific to the google maps satellite layer where it disappears when the PB layer is toggled...
    if (selectedBaseMap === 'Google Maps Satellite') {
      setTimeout(() => {
        mapRef.current?.setMapTypeId('satellite');
      }, 0);  // Small delay to allow overlay re-stack
    }

    // Also update the URL to suit.
    const url = new URL(window.location);
    url.searchParams.set('pb', isVisible ? '1' : '0');
    replaceUrlState(url);
  };


  const handleLongClick = async (latLng) => {

    const x = latLng.lng();
    const y = latLng.lat();

    try {
      const parcelResult = await fetchParcelBoundaryFromLINZ(y, x); // note: fetch expects lat, lng

      if (parcelResult) {
        const { geometry, landProps } = parcelResult;

        // Pass this to the right side bar so we can populate that info.
        if (typeof onLandSelect === 'function') {
          //console.log('onLandSelect called with:', landProps);
          onLandSelect(landProps);
        }

        // And now let's get our crayons out and draw the parcel boundaries on the map.
        if (geometry?.type === 'Polygon') {

          //console.log('Drawing Polygon boundaries:', geometry.coordinates);
          drawParcelBoundaries(mapRef.current, geometry.coordinates);
        }
        else if (geometry?.type === 'MultiPolygon') {

          // Handle MultiPolygon: loop through all polygon parts
          //console.log('Drawing MultiPolygon boundaries:', geometry.coordinates);
          geometry.coordinates.forEach(polygon => {
            drawParcelBoundaries(mapRef.current, polygon); // each polygon is in [ [ [lng, lat], ... ] ] format
          });
        }

        // On success, also write the long clicked coordinates to the URL so we can use them on reload.
        const url = new URL(window.location);
        url.searchParams.set('lilat', latLng.lat().toFixed(6));
        url.searchParams.set('lilng', latLng.lng().toFixed(6));
        replaceUrlState(url);
      }

    } catch (error) {
      console.error('Error querying LINZ:', error);
    }
  };


  useEffect(() => {  // Update mapCenter if centerProp changes
    if (centerProp) {
      setMapCenter(centerProp);
    }
  }, [centerProp]);


  // Clear the parcel boundaries when the user clicks the button in the right sidebar.
  const clearLandOverlay = () => {
    clearParcel();
  };


  // Options for the Google Map
  const options = {
    //mapTypeId: 'satellite',     // Initial map type.
    mapTypeId: 'roadmap',     // Initial map type.
    styles: washedOutMapStyle,  // Apply custom washed out style to the map. Note additional controls at the end of washedOutMapStyle.js.
    maxZoom: 22,  // Increase max zoom level.
    minZoom: 6,    // Decrease min zoom level.
    tilt: 0,                    // No tilt.
    //rotateControl: false,       // Disable rotate control.
    //mapTypeControl: false,      // Disable map type control.
    //fullscreenControl: false,   // Disable full screen control.
    //streetViewControl: false,   // Disable street view control.
    disableDefaultUI: true,       // Catch-all setting for the normal GM UI.
    clickableIcons: false,        // Make points of interest unclickable.
    ...(presentationModeiFrame && { gestureHandling: 'greedy' })  // Prevents "use two fingers to move the map" when presented in an iframe.
    //styles: [  // Note this is at the end of washedOutMapStyle.js now.
    //  {
    //    featureType: 'all',
    //    elementType: 'labels',
    //    stylers: [{ visibility: 'on' }]  // Ensure labels are visible
    //  }
    //]
  };


  /*-----------------------------------------------------------------------------*/


  if (!isLoaded) return null;
  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}  // CSS styles for the map container, ensuring it takes the full width and height of its parent
        center={mapCenter}  // Initial center coordinates of the map
        zoom={zoomParam || 20}  // Initial zoom level of the map. Set it to 20 unless there is a zoom parameter in the URL.
        options={options}
        onLoad={(map) => {  // Event handler for when the map has finished loading
          mapRef.current = map;  // Store the map instance in the mapRef to be used later in other functions
          setupMapListeners(map);  // For URL updates. Is in the urlSyncHelper.js file.
          setMapsLoaded(true);

          // Only zoom to New Zealand as an initial map view if no lat/lng/zoom provided in the URL.
          const hasValidUrlCoords = latParamFloat && lngParamFloat && zoomParam;

          if (!hasValidUrlCoords) {
            const bounds = new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(newZealandBounds.south, newZealandBounds.west),
              new window.google.maps.LatLng(newZealandBounds.north, newZealandBounds.east)
            );
            map.fitBounds(bounds);
          }

          // Reinstate the land info and parcel boundary drawings if there are lat/lng params in the URL.
          const lilat = parseFloat(urlParams.get('lilat'));
          const lilng = parseFloat(urlParams.get('lilng'));
          const hasLandParams = !isNaN(lilat) && !isNaN(lilng);

          if (hasLandParams) {
            const fakeLatLng = new window.google.maps.LatLng(lilat, lilng);
            handleLongClick(fakeLatLng); // trigger land fetch + draw
          }

          // Add listeners for the long click event for land parcel queries.
          map.addListener('mousedown', (e) => {

            if (e.domEvent && e.domEvent.touches && e.domEvent.touches.length > 1) {
              //  It's a multi-touch (pinch zoom on mobile) - ignore!
              return;
            }

            // Clear any old timer before starting a new one
            if (longClickTimeoutRef.current) {
              clearTimeout(longClickTimeoutRef.current);
              longClickTimeoutRef.current = null;
            }

            clickStartCoordsRef.current = e.latLng;

            longClickTimeoutRef.current = setTimeout(() => {
              handleLongClick(e.latLng);
              longClickTimeoutRef.current = null;
            }, 700);
          });

          const mapDiv = map.getDiv();

          if (presentationModeiFrame) {

            mapDiv.style.userSelect = 'none';  // Prevent Safari interpreting touches as selection events.
            mapDiv.style.webkitTouchCallout = 'none';  // Prevent Safari interpreting touches as selection events.

            // Prevent Safari interpreting touches as selection events.
            mapDiv.addEventListener('touchstart', (e) => {
              if (  // Exlcude these modals from the touch propagation so we can still interact with them in an iframe.
                e.touches.length === 1 &&
                e.cancelable &&
                !e.target.closest('.ui-top-buttons') &&
                !e.target.closest('.ui-bottom-buttons') &&
                !e.target.closest('.ui-share-modal')
              ) {
                e.preventDefault();  // Suppress Safari long-press
              }
            }, { passive: false });
          }

          // New protection: cancel long press if a second finger appears
          mapDiv.addEventListener('touchstart', (e) => {
            if (e.touches && e.touches.length > 1) {
              clearTimeout(longClickTimeoutRef.current);
            }
          });

          map.addListener('mouseup', () => {
            clearTimeout(longClickTimeoutRef.current);
          });

          map.addListener('mousemove', (e) => {
            if (!clickStartCoordsRef.current) return;

            const dx = Math.abs(e.latLng.lng() - clickStartCoordsRef.current.lng());
            const dy = Math.abs(e.latLng.lat() - clickStartCoordsRef.current.lat());

            // User moved more than a tiny threshold — likely dragging
            if (dx > 0.0001 || dy > 0.0001) {
              clearTimeout(longClickTimeoutRef.current);
            }
          });

          map.addListener('drag', () => {
            clearTimeout(longClickTimeoutRef.current);
          });


          // Workaround for iOS + iframe + portrait bug
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const isIframe = window.self !== window.top;
          const isPortrait = window.innerHeight > window.innerWidth;

          if (isIOS && isIframe && isPortrait) {
            setTimeout(() => {
              google.maps.event.trigger(map, 'resize');

              // If no manual coords were provided, re-zoom to NZ
              if (!hasValidUrlCoords) {
                const nzBounds = new window.google.maps.LatLngBounds(
                  new window.google.maps.LatLng(newZealandBounds.south, newZealandBounds.west),
                  new window.google.maps.LatLng(newZealandBounds.north, newZealandBounds.east)
                );
                map.fitBounds(nzBounds);
              }
            }, 500);  // Let the iframe layout settle.
          }

          //console.log('Map set up now');
          if (props.onMapLoaded) props.onMapLoaded(); // notify parent AFTER mapRef is set
        }}
      >
        {mapsLoaded && mapRef.current && (
          <Crosshairs map={mapRef.current} showCrosshairs={showCrosshairs} />
        )}

        {/* Conditionally render the button or the overlay */}
        {/* Render the DropPlanButton or the overlay based on showOverlay */}
        {/*If showOverlay is false show the button, if showOverlay is true, and there is an image, show the overlay*/}
        {
          <TopButtons
            presentationModeiFrame={presentationModeiFrame}
            onDropPlanClick={handleDropPlanClick}
            showDropPlan={!!activeOverlayPlanId && !showOverlay}
            onSearchClick={() => setIsSearchModalOpen(true)}
          />
        }
        {showOverlay && overlayImageUrl && bounds && (
          <DraggableOverlay
            map={mapRef.current}
            url={overlayImageUrl}
            bounds={bounds}
            onBoundsChanged={(newBounds) => {
              boundsRef.current = newBounds; // Just update the ref
            }}
            onDragEnd={() => {

              const newBounds = boundsRef.current;
              setBounds(newBounds);

              const centerLat = (newBounds.north + newBounds.south) / 2;
              const centerLng = (newBounds.east + newBounds.west) / 2;
              overlayCentreRef.current = { lat: centerLat, lng: centerLng };

              const url = new URL(window.location);  // Write to the URL
              url.searchParams.set('olat', centerLat.toFixed(6));
              url.searchParams.set('olng', centerLng.toFixed(6));
              url.searchParams.set('r', getCurrentRotation().toFixed(1));
              replaceUrlState(url);
            }}
            rotation={rotation}
            setRotation={setRotation}
            isShiftDraggingRef={isShiftDraggingRef}
            overlayIsMirrored={overlayIsMirrored}
          />
        )}
        {showOverlay && (
          <UIBottomButtons
            rotation={rotation}
            setRotation={setRotation}
            isShiftDraggingRef={isShiftDraggingRef}
            overlayIsMirrored={overlayIsMirrored}
            setOverlayIsMirrored={setOverlayIsMirrored}
          />
        )}
      </GoogleMap>
      {mapsLoaded && (
        <PlacesSearchModal
          open={isSearchModalOpen}
          setOpen={setIsSearchModalOpen}
          onPlaceSelected={handlePlaceSelected}
          mapsLoaded={mapsLoaded}
          map={mapRef.current}
        />
      )}
    </>
  );
});



export default MapComponent;  // Export the MapComponent to be used in other parts of the application


